/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXAtom } from '@cloudio-saas/statex';
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ImageUploadWithCropper from 'custom/ImageUploadWithCropper';
import Icon from 'icons/Icon';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import { useCallback, useEffect, useState } from 'react';
import { useStoreCurrentRecord } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { getURLQueryStringToPost } from 'utils/url';
import WaveDots from 'utils/WaveDots';
import { getUrl } from '../../config/config';
import { maskPageSignal } from 'components/Page/MaskPage';
import he from 'he';
export interface OrgRecord {
  orgUid: string;
  orgName: string;
  subDomain: string;
  logoImage: string;
  emailSignature: string;
  publicMd: Record<string, unknown>; // TODO: fix value type to support all types //
  authProvider: string;
  authEncryptionAlgo: string;
  loginBgImage: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '40px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  footer: {
    padding: 12,
    backgroundColor: theme.palette.background.default,
  },
  footerbtn: {},
  logo: {
    maxHeight: 32,
    marginRight: 12,
    verticalAlign: 'middle',
  },
  logoEdit: {
    height: theme.spacing(32),
    width: theme.spacing(32),
  },
}));

const OrgLogoEditor = () => {
  const classes = useStyles();
  const platform = usePlatform();
  const [pending, setPending] = usePending();
  const [editing, setEditing] = useState(false);
  const store = useDataStore<OrgRecord>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoOrganizations',
    alias: 'IoOrgsLogo',
  });
  useEffect(() => {
    platform.startAsyncTransition(async () => {
      await store.query();
    }, setPending);
  }, [platform, setPending, store]);

  const { record, update } = useStoreCurrentRecord(store);

  const _onEdit = () => {
    setEditing(true);
  };
  const _onEditEnd = () => {
    setEditing(false);
  };
  const _onSave = async () => {
    maskPageSignal.postCount.set((v) => v + 1);
    await store
      .save()
      .finally(() => maskPageSignal.postCount.set((v) => v - 1));
    _onEditEnd();
  };
  const _onCancel = () => {
    store.reset();
    _onEditEnd();
  };
  if (!record && !pending) {
    return <>Missing Org record!</>;
  }
  const editButton = editing ? null : (
    <Button startIcon={<Icon iconName="edit" />} onClick={_onEdit}>
      Edit Org Logo
    </Button>
  );
  const body = (
    <ImageUploadWithCropper
      key="orgLogo"
      id="orgLogo"
      label={`${record?.orgName ?? 'CloudIo'}Logo`}
      value={record?.logoImage ?? 'images/io_icon.png'}
      onChange={(val) => update({ logoImage: val })}
      isDirty={store.isStoreDirty}
      onSave={_onSave}
      onCancel={_onCancel}
    />
  );

  return pending ? (
    <WaveDots active />
  ) : (
    <>
      <TextField
        fullWidth
        variant="outlined"
        label="System Name"
        value={record?.orgName ?? ''}
        onChange={(e) => update({ orgName: e.target.value })}
        onBlur={() => {
          if (store.isStoreDirty()) {
            store.save({ feedback: 'System name updated!' });
          }
        }}
      />
      <TextField
        sx={{ marginTop: 1 }}
        fullWidth
        variant="outlined"
        label="System's Email Signature"
        value={record?.emailSignature ?? ''}
        onChange={(e) => update({ emailSignature: e.target.value })}
        onBlur={() => {
          if (store.isStoreDirty()) {
            store.save({ feedback: "System's email signature updated!" });
          }
        }}
      />
      <TextField
        sx={{ marginTop: 1 }}
        multiline
        fullWidth
        variant="outlined"
        label="Login Help Text"
        value={record?.publicMd?.loginHelpText ?? ''}
        onChange={(e) =>
          update({
            publicMd: {
              ...record?.publicMd,
              loginHelpText: e.target.value,
            },
          })
        }
        onBlur={() => {
          if (store.isStoreDirty()) {
            store.save({ feedback: "System's login help text updated!" });
          }
        }}
      />
      <TextField
        sx={{ marginTop: 1 }}
        fullWidth
        variant="outlined"
        label="Session Lifetime Minutes"
        value={record?.publicMd?.sessionLifetimeMinutes ?? 10080}
        onChange={(e) =>
          update({
            publicMd: {
              ...record?.publicMd,
              sessionLifetimeMinutes: Number(e.target.value),
            },
          })
        }
        onBlur={() => {
          if (store.isStoreDirty()) {
            const value = record?.publicMd?.sessionLifetimeMinutes ?? 0;
            if ((value as number) <= 0) {
              platform.showError(
                'Session Lifetime Minutes value must be greater than 0',
              );
              return;
            }
            if (`${value}`.includes('.')) {
              platform.showError(
                "Session Lifetime Minutes value shouldn't be in decimals",
              );
              return;
            }
            store.save({
              feedback: `Session lifetime minutes updated to ${value} !`,
            });
          }
        }}
        type="number"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={(record?.publicMd?.enableCaptcha as boolean) ?? true}
            onChange={(e) => {
              update({
                publicMd: e.target.checked
                  ? { ...record?.publicMd, enableCaptcha: true }
                  : { ...record?.publicMd, enableCaptcha: false },
              });
              if (store.isStoreDirty()) {
                store.save({ feedback: 'Enable captcha updated!' });
              }
            }}
            // onBlur={() => {
            //   if (store.isStoreDirty()) {
            //     store.save({ feedback: 'Enable captcha updated!' });
            //   }
            // }}
            color="primary"
          />
        }
        label="Enable Captcha"
      />
      <FormControlLabel
        control={
          <Tooltip title="If enabled, clicking the Home icon will redirect users with a single app directly to that Application home page instead of /home.">
            <Checkbox
              checked={
                (record?.publicMd?.singlePageRedirect as boolean) ?? false
              }
              onChange={(e) => {
                update({
                  publicMd: e.target.checked
                    ? { ...record?.publicMd, singlePageRedirect: true }
                    : { ...record?.publicMd, singlePageRedirect: false },
                });
                if (store.isStoreDirty()) {
                  store.save({ feedback: 'Single Page Redirect updated!' });
                }
              }}
              color="primary"
            />
          </Tooltip>
        }
        label="Single Page Redirect"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={
              (record?.publicMd?.allowPatchingForCloudIOApp as boolean) ?? false
            }
            onChange={(e) => {
              update({
                publicMd: e.target.checked
                  ? { ...record?.publicMd, allowPatchingForCloudIOApp: true }
                  : { ...record?.publicMd, allowPatchingForCloudIOApp: false },
              });
              if (store.isStoreDirty()) {
                store.save({
                  feedback: 'Allow Patching For CloudIO App updated!',
                });
              }
            }}
            // onBlur={() => {
            //   if (store.isStoreDirty()) {
            //     store.save({
            //       feedback: 'Allow Patching For CloudIO App updated!',
            //     });
            //   }
            // }}
            color="primary"
          />
        }
        label="Allow Patching For CloudIO App"
      />
      <div className={classes.root}>
        {editing ? (
          body
        ) : (
          <img
            className={classes.logoEdit}
            src={record?.logoImage ?? 'images/io_icon.png'}
            alt={`${record?.orgName ?? 'CloudIo'} Logo`}
          />
        )}
      </div>
      {editButton}
    </>
  );
};

export function OrgLogo() {
  const classes = useStyles();
  const store = useDataStore<OrgRecord>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoOrganizations',
    alias: 'IoOrgsLogo',
  });
  const { record } = useStoreCurrentRecord(store);
  const [state, setState] = useOrgNameAndLogo();
  const {
    logoImage,
    orgName,
    pending,
    publicMd,
    authProvider,
    authEncryptionAlgo,
    loginBgImage,
    isNativeEnabled,
  } = state;
  useEffect(() => {
    if (record) {
      setState({
        logoImage: record.logoImage ?? logoImage,
        orgName: record.orgName ?? orgName,
        pending,
        publicMd: record.publicMd ?? publicMd,
        authProvider: record.authProvider ?? authProvider,
        authEncryptionAlgo: record.authEncryptionAlgo ?? authEncryptionAlgo,
        loginBgImage: record.loginBgImage ?? loginBgImage,
        isNativeEnabled: isNativeEnabled ?? false,
        signInPageCss: '',
      });
    }
  }, [
    authEncryptionAlgo,
    authProvider,
    logoImage,
    orgName,
    pending,
    publicMd,
    publicMd.enableCaptcha,
    record,
    setState,
    loginBgImage,
    isNativeEnabled,
  ]);

  return pending ? (
    <WaveDots active />
  ) : (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <img
      className={classes.logo}
      src={logoImage ?? 'images/io_icon.png'}
      alt={`${orgName ?? 'CloudIo'} Logo`}
    />
  );
}

interface NameAndLogo {
  signInPageCss?: string;
  logoImage: string;
  orgName: string;
  pending: boolean;
  publicMd: {
    enableCaptcha?: boolean;
    sessionLifetimeMinutes?: number;
    allowPatchingForCloudIOApp?: boolean;
    loginHelpText?: string;
  };
  authProvider: string;
  authEncryptionAlgo: string;
  loginBgImage: string;
  isNativeEnabled: boolean;
}

const orgNameAndLogo: NameAndLogo = {
  logoImage: 'images/io_icon.png',
  orgName: 'Apps',
  pending: true,
  publicMd: { enableCaptcha: false },
  authProvider: 'NATIVE',
  authEncryptionAlgo: 'BASE64',
  loginBgImage: '',
  isNativeEnabled: false,
  signInPageCss: '',
};

export const orgNameAndLogoAtom = atom<NameAndLogo>({
  path: ['transient', 'orgNameAndLogo'],
  defaultValue: { ...orgNameAndLogo },
});

export const encrypt = (input: string): string => {
  return btoa(input);
};

export function useOrgNameAndLogo(): [NameAndLogo, (val: NameAndLogo) => void] {
  const [state, setState] = useStateXAtom(orgNameAndLogoAtom);
  useEffect(() => {
    async function query() {
      try {
        if (orgNameAndLogo.pending) {
          orgNameAndLogo.pending = false;
          //   console.log(getUrl() + 'public?' + getURLQueryStringToPost());
          const response = await fetch(
            `${getUrl()}public?${getURLQueryStringToPost()}`,
            {
              method: 'POST',
              headers: {
                'X-Application': encrypt('cloudio'),
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: encrypt(
                '{"a":{"ds":"IoOrgNameAndLogo","query":{"limit":1}}}',
              ),
            },
          );
          const rawText = await response.text();
          const decodedText = he.decode(rawText);
          const json = JSON.parse(decodedText);

          if (json.status === 'OK') {
            //   console.log(json.data.a.data[0]);
            const {
              logoImage,
              orgName,
              publicMd,
              authProvider,
              authEncryptionAlgo,
              loginBgImage,
              isNativeEnabled,
              signInPageCss,
            } = json.data.a.data[0] ?? orgNameAndLogo;

            if (logoImage) {
              orgNameAndLogo.logoImage = logoImage;
            }
            if (loginBgImage) {
              orgNameAndLogo.loginBgImage = loginBgImage;
            }
            if (orgName) {
              orgNameAndLogo.orgName = orgName;
            }
            if (signInPageCss) {
              orgNameAndLogo.signInPageCss = signInPageCss;
            }
            if (publicMd) {
              orgNameAndLogo.publicMd = publicMd;
            } else {
              orgNameAndLogo.publicMd = {};
            }
            if (orgNameAndLogo.publicMd.enableCaptcha !== false) {
              orgNameAndLogo.publicMd.enableCaptcha = true;
            }
            orgNameAndLogo.authProvider = authProvider || 'NATIVE';
            orgNameAndLogo.authEncryptionAlgo = authEncryptionAlgo || 'BASE64';
            orgNameAndLogo.isNativeEnabled = !!isNativeEnabled;
            //  console.log(orgNameAndLogo);
            setState({ ...orgNameAndLogo });
          }
        }
      } catch (e) {
        if (console) console.error(e);
      }
    }
    query();
  }, [setState]);

  const setValue = useCallback(
    (val: NameAndLogo) => {
      const { logoImage, orgName, loginBgImage } = val;
      if (logoImage) {
        orgNameAndLogo.logoImage = logoImage;
      }
      if (loginBgImage) {
        orgNameAndLogo.loginBgImage = loginBgImage;
      }
      if (orgName) {
        orgNameAndLogo.orgName = orgName;
      }
      setState({ ...orgNameAndLogo });
    },
    [setState],
  );
  return [state, setValue];
}

export default OrgLogoEditor;
